/* Stat block parts shared by adversaries and environments, laid out like the
   SRD stat blocks: an inset box of stats and hanging-indented features led by
   "Name - Type:". */

import { CSSProperties, KeyboardEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { parseFeatureText, parseTextWithTerms } from '../character/content/parseText';
import '../character/content/FeatureList.css';
import './StatBlock.css';

export interface StatItem {
  label?: string;
  value: ReactNode;
}

export interface StatFeatureData {
  name?: string;
  type?: string;
  cost?: string;
  text: ReactNode;
  bullets?: ReactNode[];
  questions?: string[];
}

/** Rows of "Label: value" items, split by thin gilt rules, with an optional footer line. */
export function StatBox({ rows, footer }: { rows: StatItem[][]; footer?: ReactNode }) {
  return (
    <div className="statbox">
      {rows.map((row, i) => (
        <p key={i} className="statbox__row">
          <span className="statbox__items">
            {row.map((item, j) => (
              <span key={j} className="statbox__item">
                {item.label && <strong>{item.label}:</strong>} {item.value}
              </span>
            ))}
          </span>
        </p>
      ))}
      {footer && <p className="statbox__footer">{footer}</p>}
    </div>
  );
}

export interface StatBrowserGroup<T> {
  key: string;
  label?: ReactNode;
  items: T[];
}

const itemId = (key: string) => `stat-item-${key.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

/** A compact, scrollable list of cards on one side and the chosen card in full
    on the other (the first card until one is picked). Arrow keys move through
    the list. On phones the list fills the screen and a pick opens the card in a
    full-screen panel with a back button. */
export function StatBrowser<T>({ groups, keyOf, renderItem, renderCard, accentOf, listLabel, closeLabel }: {
  groups: StatBrowserGroup<T>[];
  keyOf: (item: T) => string;
  renderItem: (item: T) => ReactNode;
  renderCard: (item: T) => ReactNode;
  accentOf?: (item: T) => string;
  listLabel: string;
  closeLabel: string;
}) {
  const items = groups.flatMap((g) => g.items);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const selected = items.find((item) => keyOf(item) === selectedKey) ?? items[0];
  const current = selected === undefined ? null : keyOf(selected);

  useEffect(() => {
    panelRef.current?.scrollTo?.({ top: 0 });
  }, [current]);

  const move = (delta: number) => {
    const next = items[items.findIndex((item) => keyOf(item) === current) + delta];
    if (next === undefined) return;
    setSelectedKey(keyOf(next));
    document.getElementById(itemId(keyOf(next)))?.focus();
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      move(e.key === 'ArrowDown' ? 1 : -1);
    }
  };

  return (
    <div className={`statview${open ? ' statview--open' : ''}`}>
      <nav className="statview__list" aria-label={listLabel} onKeyDown={onKeyDown}>
        {groups.filter((g) => g.items.length > 0).map((g) => (
          <section key={g.key} className="statview__group">
            {g.label && <h3 className="statview__group-label">{g.label}</h3>}
            <ul>
              {g.items.map((item) => {
                const key = keyOf(item);
                return (
                  <li key={key}>
                    <button
                      id={itemId(key)}
                      type="button"
                      className="statview__item"
                      aria-current={key === current ? 'true' : undefined}
                      style={accentOf ? { '--item-accent': accentOf(item) } as CSSProperties : undefined}
                      onClick={() => { setSelectedKey(key); setOpen(true); }}
                    >
                      {renderItem(item)}
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </nav>
      <div className="statview__panel" ref={panelRef}>
        <button type="button" className="statview__close" onClick={() => setOpen(false)}>&larr; {closeLabel}</button>
        {selected !== undefined && renderCard(selected)}
      </div>
    </div>
  );
}

function Bullet({ text }: { text: ReactNode }) {
  if (typeof text !== 'string') return <li>{text}</li>;
  const colon = text.indexOf(':');
  if (colon > 0 && colon < 40) {
    return (
      <li>
        <strong>{text.slice(0, colon).trim()}:</strong> {parseTextWithTerms(text.slice(colon + 1).trim())}
      </li>
    );
  }
  return <li>{parseTextWithTerms(text)}</li>;
}

export function StatFeature({ feature }: { feature: StatFeatureData }) {
  const { name, type, cost, text, bullets, questions } = feature;
  const plain = [typeof text === 'string' ? text : '', ...(bullets ?? []).filter((b): b is string => typeof b === 'string')].join(' ');
  const hasFear = cost ? /fear/i.test(cost) : /spend a fear|spend \d+ fear/i.test(plain);
  const hasStress = cost ? /stress/i.test(cost) : /mark a stress|mark \d+ stress/i.test(plain);
  const hasHope = cost ? /hope/i.test(cost) : /spend a hope|spend \d+ hope/i.test(plain);
  return (
    <div className={`statfeature statfeature--${type ? type.toLowerCase() : 'plain'}`}>
      <div className="statfeature__text">
        {name && <strong className="statfeature__lead">{type ? `${name} - ${type}:` : `${name}:`}</strong>}
        {hasHope && <span className="statfeature__tag statfeature__tag--hope">HOPE</span>}
        {hasFear && <span className="statfeature__tag statfeature__tag--fear">FEAR</span>}
        {hasStress && <span className="statfeature__tag statfeature__tag--stress">STRESS</span>}{' '}
        {cost && <strong>{cost} </strong>}
        {parseFeatureText(text)}
      </div>
      {bullets && bullets.length > 0 && (
        <ul className="statfeature__bullets">
          {bullets.map((b, i) => <Bullet key={i} text={b} />)}
        </ul>
      )}
      {questions && questions.length > 0 && <p className="statfeature__questions">{questions.join(' ')}</p>}
    </div>
  );
}
